import pick from 'lodash/pick';
import throttle from 'lodash/throttle';
import { type Action, type AnyAction, type Store } from 'redux';
import StorageAPI from 'src/core/utils/StorageAPI';
import isShallowEqual from 'utils/isShallowEqual';
import { STORED_REDUX_STORES } from 'vd-constants';

/**
 * Load initial values for stores from local storage.
 *
 * @param persistentStores - A list of store names to load.  These must be
 *   top level keys in the Redux state hierarchy.
 */
export function loadStores<S>(persistentStores: readonly (keyof S)[]): any | undefined {
  const stored = StorageAPI.getLocally(STORED_REDUX_STORES) as any | null;
  if (stored) {
    return pick(stored, persistentStores);
  }
  return undefined;
}

/**
 * Subscribe to store and persist some state to local storage on changes.
 *
 * @param store - The store to subscribe to.
 * @param persistentStores - A list of store names to load.  These must be
 *   top level keys in the Redux state hierarchy.
 * @param [options.throttleDelay=1000] - To avoid spamming the local storage API,
 *   writes will be throttled using this delay.
 */
export function persistStores<S, T extends keyof S, A extends Action = AnyAction>(
  store: Store<S, A>,
  persistentStores: readonly T[],
  options: { throttleDelay?: number } = {},
) {
  const throttleDelay = options.throttleDelay ?? 1000;
  let previousState = pick(store.getState(), persistentStores);

  const subscriber = throttle(
    () => {
      const newState = pick(store.getState(), persistentStores);

      // Only write to local storage if something has actually changed.
      if (!isShallowEqual(previousState, newState)) {
        previousState = newState;
        StorageAPI.storeLocally(STORED_REDUX_STORES, newState);
      }
    },
    throttleDelay,
    { leading: true, trailing: true },
  );

  store.subscribe(subscriber);
}
